import React from 'react';
import { AnalyticsIcon } from '../Icons/OverviewIcons.jsx';
import './CargoInsights.css';

const CargoInsights = ({ cargos = [], cargoArea = [], selectedRows = new Set() }) => {
  const insights = React.useMemo(() => {
    if (cargos.length === 0) {
      return { 
        message: "No cargos to analyze",
        sizeRanges: {},
        avgDimensions: { width: 0, height: 0, depth: 0 },
        totalCargos: 0,
        totalCargoVolume: 0,
        cargoAreaVolume: 0,
        totalCargoVolumeInside: 0,
        totalCargoVolumeOutside: 0,
        remainsCargoAreaVolume: 0,
        utilizationCargoAreaRate: 0,
        utilizationCargoListRate: 0,
      };
    }

    const sizeRanges = {
      'Small': { count: 0, max: 15, color: '#10b981' },
      'Medium': { count: 0, max: 120000, color: '#3b82f6' },
      'Large': { count: 0, max: 125000, color: '#f59e0b' },
      'X-Large': { count: 0, max: Infinity, color: '#ef4444' }
    };

    cargos.forEach(cargo => {
      const volume = (cargo.widthX * cargo.heightY * cargo.depthZ) / 1000000;
      if (volume < 15) sizeRanges['Small'].count++;
      else if (volume < 120000) sizeRanges['Medium'].count++;
      else if (volume < 125000) sizeRanges['Large'].count++;
      else sizeRanges['X-Large'].count++;
    });

    const avgWidth = cargos.reduce((sum, cargo) => sum + cargo.widthX, 0) / cargos.length;
    const avgHeight = cargos.reduce((sum, cargo) => sum + cargo.heightY, 0) / cargos.length;
    const avgDepth = cargos.reduce((sum, cargo) => sum + cargo.depthZ, 0) / cargos.length;

    const totalCargoVolume = cargos.reduce((sum, cargo) => 
      sum + (cargo.widthX * cargo.heightY * cargo.depthZ) , 0
    );
    
    const cargoAreaVolume = cargoArea.length > 0 && cargoArea[0] 
      ? (cargoArea[0].widthX * cargoArea[0].heightY * cargoArea[0].depthZ) 
      : 0;

    const totalCargoVolumeInside = cargos.reduce((sum, cargo) => {
      if (cargo.active) {
        return sum + (cargo.widthX * cargo.heightY * cargo.depthZ);
      }
      return sum;
    }, 0);

    const totalCargoVolumeOutside = cargos.reduce((sum, cargo) => {
      if (!cargo.active) {
        return sum + (cargo.widthX * cargo.heightY * cargo.depthZ);
      }
      return sum;
    }, 0);


    const utilizationCargoAreaRate = cargoAreaVolume > 0 
      ? Math.round(totalCargoVolumeInside / cargoAreaVolume * 100) 
      : 0;

    const utilizationCargoListRate = totalCargoVolume > 0 
      ? Math.round(totalCargoVolumeInside / totalCargoVolume * 100) 
      : 0;


    return {
      sizeRanges,
      avgDimensions: {
        width: Math.round(avgWidth),
        height: Math.round(avgHeight),
        depth: Math.round(avgDepth)
      },
      totalCargos: cargos.length,
      totalCargoVolume: Math.round(totalCargoVolume),
      cargoAreaVolume: Math.round(cargoAreaVolume),
      totalCargoVolumeInside: Math.round(totalCargoVolumeInside),
      totalCargoVolumeOutside: Math.round(totalCargoVolumeOutside),
      remainsCargoAreaVolume: Math.max(0, Math.round(cargoAreaVolume-totalCargoVolumeInside)),
      utilizationCargoAreaRate,
      utilizationCargoListRate
    };
  }, [cargos, cargoArea]);

  const DistributionChart = () => (
    <div className="distribution-chart">
      <div className="chart-header">
        <h4 className="chart-title">Cargo Size Distribution</h4>
        <div className="chart-total">{insights.totalCargos} items</div>
      </div>
      
      <div className="chart-bars">
        {Object.entries(insights.sizeRanges).map(([size, data]) => {
          const percentage = insights.totalCargos > 0 
            ? (data.count / insights.totalCargos) * 100 
            : 0;
          
          return (
            <div key={size} className="chart-bar-item">
              <div className="bar-info">
                <span className="bar-label">{size}</span>
                <span className="bar-value">{data.count}</span>
              </div>
              <div className="bar-track-modern">
                <div 
                  className="bar-fill-modern"
                  style={{ 
                    width: `${percentage}%`,
                    backgroundColor: data.color
                  }}
                >
                  <div className="bar-glow"></div>
                </div>
              </div>
              <span className="bar-percentage">{percentage.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const DimensionCard = ({ label, value, unit = 'mm' }) => (
    <div className="dimension-card">
      <div className="dimension-value">{value}</div>
      <div className="dimension-label">{label}</div>
      <div className="dimension-unit">{unit}</div>
    </div>
  );

  return (
    <div className="cargo-insights-modern">
      {insights.totalCargos === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <AnalyticsIcon size={48} color="#64748b" />
          </div>
          <h4>No Data Available</h4>
          <p>Add cargos to see insights and analytics</p>
        </div>
      ) : (
        <>

          {/* Volume Progress */}
          <div className="volume-progress-modern">

            <div className="progress-header">
              <span className="progress-title">Space Utilization</span>
              <span className="progress-value">{insights.utilizationCargoAreaRate}%</span>
            </div>


            <div className="progress-bar-modern">
              <div 
                className="progress-fill-modern"
                style={{ width: `${Math.min(100, insights.utilizationCargoAreaRate)}%` }}
                data-value={insights.utilizationCargoAreaRate}
              >
                <div className="progress-glow"></div>
              </div>
            </div>



            <div className="progress-stats">

              <div className="progress-stat">
                <span className="stat-label">Total deck space:</span>
                <span className="stat-value">{(insights.cargoAreaVolume / 1000000000).toFixed(2)} m³</span>
              </div>

              <div className="progress-stat">
                <span className="stat-label">Cargo inside & outside:</span>
                <span className="stat-value">{(insights.totalCargoVolume/1000000000).toFixed(2)} m³</span>
              </div>
              
              <div className="progress-stat">
                <span className="stat-label">Cargo inside:</span>
                <span className="stat-value">{(insights.totalCargoVolumeInside/1000000000).toFixed(2)} m³</span>
              </div>






              <div className="progress-stat">
                <span className="stat-label">Cargo outside:</span>
                <span className="stat-value">{(insights.totalCargoVolumeOutside/1000000000).toFixed(2)} m³</span>
              </div>


            </div>
          </div>




          <DistributionChart />
          
          <div className="dimensions-section">
            <h4 className="section-title-modern">Average Dimensions</h4>
            <div className="dimensions-grid-modern">
              <DimensionCard 
                label="Width" 
                value={insights.avgDimensions.width} 
              />
              <DimensionCard 
                label="Height" 
                value={insights.avgDimensions.height} 
              />
              <DimensionCard 
                label="Depth" 
                value={insights.avgDimensions.depth} 
              />
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default CargoInsights;